import { useEffect, useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { RequireSession } from './components/RequireSession';
import { SyncStatusBar } from './components/SyncStatusBar';
import { UpdateBanner } from './components/UpdateBanner';
import { AdHocCapturePage } from './pages/AdHocCapturePage';
import { CloseEventPage } from './pages/CloseEventPage';
import { EventDetailPage } from './pages/EventDetailPage';
import { EventsPage } from './pages/EventsPage';
import { LandingPage } from './pages/LandingPage';
import { PairPage } from './pages/PairPage';
import { SettingsPage } from './pages/SettingsPage';
import { UnlockPage } from './pages/UnlockPage';
import { WizardPage } from './pages/WizardPage';

/**
 * Top-level shell. Three banners stack at the top: app-update toast,
 * sync status, then the routed page below.
 *
 * Routing for v1
 *   /         → LandingPage (decides where to go based on device state)
 *   /pair     → PairPage (one-time email + password + PIN)
 *   /unlock   → UnlockPage (PIN or WebAuthn)
 *   later: /events, /events/:id, /events/:id/wizard, /queue, /settings
 */
export function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    const onUpdate = () => setUpdateAvailable(true);
    window.addEventListener('argos:sw-update-available', onUpdate);
    return () => window.removeEventListener('argos:sw-update-available', onUpdate);
  }, []);

  return (
    <div className="min-h-full flex flex-col bg-mono-950 text-mono-100">
      {updateAvailable && (
        <UpdateBanner
          onAccept={async () => {
            await window.__argosUpdateSW?.(true);
            setUpdateAvailable(false);
          }}
        />
      )}
      <SyncStatusBar />
      <main className="flex-1 flex flex-col">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pair" element={<PairPage />} />
          <Route path="/unlock" element={<UnlockPage />} />
          <Route
            path="/events"
            element={
              <RequireSession>
                <EventsPage />
              </RequireSession>
            }
          />
          <Route
            path="/events/:eventId"
            element={
              <RequireSession>
                <EventDetailPage />
              </RequireSession>
            }
          />
          <Route
            path="/events/:eventId/wells/:wellId/wizard"
            element={
              <RequireSession>
                <WizardPage />
              </RequireSession>
            }
          />
          <Route
            path="/events/:eventId/close"
            element={
              <RequireSession>
                <CloseEventPage />
              </RequireSession>
            }
          />
          <Route
            path="/capture"
            element={
              <RequireSession>
                <AdHocCapturePage />
              </RequireSession>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireSession>
                <SettingsPage />
              </RequireSession>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          // Match the PWA's surface treatment so the toast feels native.
          style: {
            background: 'rgba(15, 23, 42, 0.96)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            color: '#f1f5f9',
            backdropFilter: 'blur(8px)',
          },
        }}
      />
    </div>
  );
}
